import { Request, Response } from '@luxuryescapes/contract-svc-tour';

export type Supplier = Response.Supplier;

export type ServiceKind = Request.ServiceKind;

export const SERVICE_KINDS: ReadonlyArray<ServiceKind> = ['Tour', 'Accommodation', 'OptionalExperience', 'Flight'];

export type ServiceCostPriceType = Request.ServiceCostPriceType | 'FixedPerGroup';

export type ServiceCostPriceScope = Request.ServiceCostPriceScope;

export const SERVICE_COST_PRICE_TYPES: ReadonlyArray<ServiceCostPriceType> = ['SlidingScale', 'Fixed', 'FixedPerGroup'];

export const SERVICE_COST_PRICE_SCOPES: ReadonlyArray<ServiceCostPriceScope> = ['Global', 'Departure'];

export const SERVICE_COST_PRICE_TYPE_LABELS: Record<ServiceCostPriceType, string> = {
  SlidingScale: 'Sliding scale',
  Fixed: 'Fixed',
  FixedPerGroup: 'Fixed per group',
};

export const SERVICE_COST_PRICE_SCOPE_LABELS: Record<ServiceCostPriceScope, string> = {
  Global: 'Whole tour',
  Departure: 'Per departure',
};

const NOT_COSTED = '—';

export function costPriceTypeLabel(type?: ServiceCostPriceType): string {
  return type ? SERVICE_COST_PRICE_TYPE_LABELS[type] : NOT_COSTED;
}

export function costPriceScopeLabel(scope?: ServiceCostPriceScope): string {
  return scope ? SERVICE_COST_PRICE_SCOPE_LABELS[scope] : NOT_COSTED;
}

/** How a service is costed. Discarding is required once it holds cost prices, which the change removes. */
export interface UpdateServiceCostingPayload {
  costPriceType: ServiceCostPriceType;
  costPriceScope: ServiceCostPriceScope;
  discardCostPrices?: boolean;
}

export type CreateServicePayload = Omit<Request.PostServiceBody, 'costPriceType'> & {
  costPriceType?: ServiceCostPriceType;
};

export type CostPriceKind = Request.CostPriceKind | 'FixedPerGroup' | 'FixedPerGroupByDeparture';

const COST_PRICE_KIND_BY_TYPE_AND_SCOPE: Record<`${ServiceCostPriceType}-${ServiceCostPriceScope}`, CostPriceKind> = {
  'SlidingScale-Global': 'SlidingScale',
  'SlidingScale-Departure': 'SlidingScaleByDeparture',
  'FixedPerGroup-Global': 'FixedPerGroup',
  'FixedPerGroup-Departure': 'FixedPerGroupByDeparture',
  'Fixed-Global': 'Fixed',
  'Fixed-Departure': 'FixedByDeparture',
};

/**
 * Mirrors svc-tour: kind comes from the service, not the row. Absent fields
 * fall back to the same column defaults svc-tour stores, so a service served
 * without them behaves as the one it would have created.
 */
export function costPriceKindFromService(service: {
  costPriceType?: ServiceCostPriceType;
  costPriceScope?: ServiceCostPriceScope;
}): CostPriceKind {
  const type = service.costPriceType ?? 'SlidingScale';
  const scope = service.costPriceScope ?? 'Global';

  return COST_PRICE_KIND_BY_TYPE_AND_SCOPE[`${type}-${scope}`];
}

/** Writing a kind the service does not declare is accepted, then never read back. */
export function servicePricing(service: {
  costPriceType?: ServiceCostPriceType;
  costPriceScope?: ServiceCostPriceScope;
}) {
  const kind = costPriceKindFromService(service);
  const isSlidingScale = kind === 'SlidingScale' || kind === 'SlidingScaleByDeparture';

  return {
    kind,
    canUploadSlidingScale: isSlidingScale,
    canSetFixedPrice: !isSlidingScale,
    /** Prices are keyed by departure, so the tab offers the departure view. */
    isByDeparture:
      kind === 'SlidingScaleByDeparture' || kind === 'FixedByDeparture' || kind === 'FixedPerGroupByDeparture',
  };
}
